import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, CheckCircle2, ShieldAlert, ArrowRight, PenTool } from "lucide-react";
import { RelojProcesal } from "@/services/scraperApi";

interface RelojProcesalCardProps {
  reloj?: RelojProcesal;
  onActionClick?: () => void;
  onGenerateDraft?: (task: any) => void;
}

export const RelojProcesalCard: React.FC<RelojProcesalCardProps> = ({ reloj, onActionClick, onGenerateDraft }) => {
  if (!reloj) return null;

  const diasRestantesNum = typeof reloj.dias_restantes === "number" 
    ? reloj.dias_restantes 
    : parseInt(String(reloj.dias_restantes || "0"), 10);
  
  const isVencido = !isNaN(diasRestantesNum) && diasRestantesNum < 0;
  const isUrgente = reloj.alerta_termino === "URGENTE_ACCION_INMEDIATA" || isVencido;
  const isObservacion = reloj.alerta_termino === "EN_OBSERVACION";

  const actionTitle = reloj.termino_critico?.includes("Libertad")
    ? "Solicitud de Libertad Inmediata por Vencimiento de Términos (Art. 317 CPP)"
    : reloj.termino_critico?.includes("Caducidad")
    ? "Memorial de Excepción Previa o Impulso por Caducidad (Art. 164 CPACA)"
    : "Memorial de Impulso Procesal por Términos Perentorios";

  return (
    <Card className={`p-6 border transition-all duration-300 relative overflow-hidden shadow-sm ${
      isUrgente 
        ? "bg-gradient-to-br from-rose-50/80 via-white to-rose-50/30 border-rose-200 shadow-rose-100" 
        : isObservacion
        ? "bg-gradient-to-br from-amber-50/80 via-white to-amber-50/30 border-amber-200 shadow-amber-100"
        : "bg-gradient-to-br from-emerald-50/80 via-white to-emerald-50/30 border-emerald-200 shadow-emerald-100"
    }`}>
      {/* Background ambient glow */}
      <div className={`absolute -right-10 -top-10 w-40 h-40 rounded-full blur-3xl pointer-events-none opacity-20 ${
        isUrgente ? "bg-rose-500" : isObservacion ? "bg-amber-500" : "bg-emerald-500"
      }`} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className={`p-2.5 rounded-xl ${
            isUrgente 
              ? "bg-rose-100 text-rose-700 animate-pulse" 
              : isObservacion 
              ? "bg-amber-100 text-amber-700" 
              : "bg-emerald-100 text-emerald-700"
          }`}>
            <Clock className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider">
                Reloj Procesal y Términos Judiciales
              </h3>
              <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-none text-[10px] font-bold px-2 py-0.5">
                Calendario Judicial Colombia
              </Badge>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Cómputo estricto descontando festivos (Ley Emiliani) y vacancia judicial (Ley 270/96)
            </p>
          </div>
        </div>

        <div>
          <Badge className={`px-3 py-1 font-bold text-xs uppercase tracking-wider border-none ${
            isUrgente 
              ? "bg-rose-600 text-white shadow-sm shadow-rose-200 animate-pulse" 
              : isObservacion
              ? "bg-amber-500 text-white shadow-sm shadow-amber-200"
              : "bg-emerald-600 text-white"
          }`}>
            {isUrgente ? "Alerta Crítica: Vencimiento" : isObservacion ? "Término en Observación" : "En Término Ordinario"}
          </Badge>
        </div>
      </div>

      {/* Grid Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-4">
        {/* Término Analizado */}
        <div className="p-3.5 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100/80 shadow-xs">
          <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">
            Término Legal en Evaluación
          </span>
          <p className="text-xs font-bold text-gray-900 leading-snug">
            {reloj.termino_critico || "Término ordinario en trámite"}
          </p>
        </div>

        {/* Días Hábiles Transcurridos */}
        <div className="p-3.5 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100/80 shadow-xs">
          <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">
            Días Hábiles Judiciales
          </span>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-black text-gray-900">
              {reloj.dias_habiles_transcurridos ?? 0}
            </span>
            <span className="text-xs text-gray-500 font-medium">días transcurridos</span>
          </div>
        </div>

        {/* Días Restantes / Fenecimiento */}
        <div className={`p-3.5 rounded-xl border shadow-xs ${
          isVencido 
            ? "bg-rose-50/90 border-rose-200 text-rose-900" 
            : isObservacion 
            ? "bg-amber-50/90 border-amber-200 text-amber-900" 
            : "bg-emerald-50/90 border-emerald-200 text-emerald-900"
        }`}>
          <span className="text-[10px] uppercase font-bold tracking-wider block mb-1 opacity-80">
            {isVencido ? "Estado de Vencimiento" : "Plazo Restante"}
          </span>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-black">
              {isVencido ? `${Math.abs(diasRestantesNum)}d` : `${reloj.dias_restantes ?? "N/A"}`}
            </span>
            <span className="text-xs font-bold">
              {isVencido ? "VENCIDO (Causal configurada)" : "días para vencimiento"}
            </span>
          </div>
        </div>
      </div>

      {/* Acción Perentoria Box */}
      <div className={`p-4 rounded-xl border flex flex-col md:flex-row md:items-center justify-between gap-3 ${
        isUrgente 
          ? "bg-rose-100/60 border-rose-200/80 text-rose-950" 
          : "bg-slate-50 border-slate-200/80 text-slate-800"
      }`}>
        <div className="flex items-start gap-2.5">
          {isUrgente ? (
            <ShieldAlert className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" />
          ) : (
            <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          )}
          <div>
            <span className="text-[10px] uppercase font-black tracking-wider block text-rose-700">
              Acción Procesal Perentoria Sugerida
            </span>
            <p className="text-xs font-semibold leading-relaxed mt-0.5">
              {reloj.recomendacion_reloj || "Vigilar los estados diarios y las publicaciones de fijación en lista."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {onGenerateDraft && (
            <Button 
              onClick={() => onGenerateDraft({
                titulo: actionTitle,
                descripcion: reloj.recomendacion_reloj || "Acción urgente fundamentada en los términos procesales vencidos o por vencer.",
                objetivo: `Radicar memorial urgente ante el despacho judicial por: ${reloj.termino_critico || "términos procesales"}`
              })}
              size="sm"
              className={`rounded-xl font-bold text-xs h-9 px-4 shadow-sm flex items-center gap-1.5 ${
                isUrgente 
                  ? "bg-rose-600 hover:bg-rose-700 text-white shadow-rose-200" 
                  : "bg-legal-blue hover:bg-legal-navy text-white"
              }`}
            >
              <PenTool className="h-3.5 w-3.5" />
              <span>✍️ Redactar Memorial</span>
            </Button>
          )}

          {onActionClick && (
            <Button 
              onClick={onActionClick}
              variant="outline"
              size="sm"
              className="rounded-xl font-bold text-xs h-9 px-3 bg-white hover:bg-gray-50 border-gray-200 text-gray-700"
            >
              <span>Crear Tarea</span>
              <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};
